/**
 * useRecordings Hook
 * Manages session recordings state and API operations
 */

import { useState, useCallback } from 'react';
import { recordingsService } from '../services/recordings.service';
import type {
  SessionRecording,
  RecordingListFilters,
  UpdateRecordingRequest,
} from '../types/api.types';
import { asApiError } from '@/utils/error.util';

export interface RecordingsState {
  recordings: SessionRecording[];
  selectedRecording: SessionRecording | null;
  isLoading: boolean;
  isLoadingDetails: boolean;
  isLoadingUrl: boolean;
  error: string | null;
  total: number;
  downloadUrl: string | null;
  downloadUrlExpiresAt: string | null;
}

export const useRecordings = () => {
  const [state, setState] = useState<RecordingsState>({
    recordings: [],
    selectedRecording: null,
    isLoading: false,
    isLoadingDetails: false,
    isLoadingUrl: false,
    error: null,
    total: 0,
    downloadUrl: null,
    downloadUrlExpiresAt: null,
  });

  /**
   * Fetch recordings list with optional filters
   */
  const fetchRecordings = useCallback(async (filters?: RecordingListFilters) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await recordingsService.list(filters);

      setState(prev => ({
        ...prev,
        recordings: response.recordings,
        total: response.total,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Error fetching recordings:', error);
      setState(prev => ({
        ...prev,
        error: asApiError(error).response?.data?.error || 'Failed to load recordings',
        isLoading: false,
      }));
    }
  }, []);

  /**
   * Fetch detailed information for a specific recording
   */
  const fetchRecordingDetails = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, isLoadingDetails: true, error: null }));

    try {
      const recording = await recordingsService.get(id);

      setState(prev => ({
        ...prev,
        selectedRecording: recording,
        isLoadingDetails: false,
      }));
    } catch (error) {
      console.error('Error fetching recording details:', error);
      setState(prev => ({
        ...prev,
        error: asApiError(error).response?.data?.error || 'Failed to load recording details',
        isLoadingDetails: false,
      }));
    }
  }, []);

  /**
   * Fetch presigned download URL for a recording
   */
  const fetchDownloadUrl = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, isLoadingUrl: true, error: null }));

    try {
      const { url, expiresAt } = await recordingsService.getDownloadUrl(id);

      setState(prev => ({
        ...prev,
        downloadUrl: url,
        downloadUrlExpiresAt: expiresAt,
        isLoadingUrl: false,
      }));
    } catch (error) {
      console.error('Error fetching download URL:', error);
      setState(prev => ({
        ...prev,
        error: asApiError(error).response?.data?.error || 'Failed to get download URL',
        isLoadingUrl: false,
      }));
    }
  }, []);

  /**
   * Update recording metadata (title, description)
   */
  const updateRecording = useCallback(async (id: string, updates: UpdateRecordingRequest) => {
    setState(prev => ({ ...prev, error: null }));

    try {
      const updated = await recordingsService.update(id, updates);

      setState(prev => ({
        ...prev,
        selectedRecording: prev.selectedRecording?.id === id ? updated : prev.selectedRecording,
        recordings: prev.recordings.map(r => r.id === id ? updated : r),
      }));

      return updated;
    } catch (error) {
      console.error('Error updating recording:', error);
      setState(prev => ({
        ...prev,
        error: asApiError(error).response?.data?.error || 'Failed to update recording',
      }));
      throw error;
    }
  }, []);

  /**
   * Delete recording (admin only)
   */
  const deleteRecording = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, error: null }));

    try {
      await recordingsService.delete(id);

      setState(prev => ({
        ...prev,
        recordings: prev.recordings.filter(r => r.id !== id),
        selectedRecording: prev.selectedRecording?.id === id ? null : prev.selectedRecording,
        total: prev.total - 1,
      }));
    } catch (error) {
      console.error('Error deleting recording:', error);
      setState(prev => ({
        ...prev,
        error: asApiError(error).response?.data?.error || 'Failed to delete recording',
      }));
      throw error;
    }
  }, []);

  /**
   * Select a recording for viewing
   */
  const selectRecording = useCallback((recording: SessionRecording | null) => {
    setState(prev => ({
      ...prev,
      selectedRecording: recording,
      downloadUrl: null, // Clear download URL when switching recordings
      downloadUrlExpiresAt: null,
    }));
  }, []);

  /**
   * Clear download URL (e.g., when switching recordings)
   */
  const clearDownloadUrl = useCallback(() => {
    setState(prev => ({
      ...prev,
      downloadUrl: null,
      downloadUrlExpiresAt: null,
    }));
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    fetchRecordings,
    fetchRecordingDetails,
    fetchDownloadUrl,
    updateRecording,
    deleteRecording,
    selectRecording,
    clearDownloadUrl,
    clearError,
  };
};
